import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { clientTokenCache } from '../services/auth/ClientTokenCache';
import { dspAuthService } from '../services/auth/DSPAuthService';
import spotlightService from '../services/SpotlightService';
import analytics from '../services/analytics';

const AuthContext = createContext(null);

const getCurrentPath = () => `${window.location.pathname}${window.location.search}`;

const toLoginPath = returnTo => {
  const target = returnTo || getCurrentPath();
  return `/login?returnTo=${encodeURIComponent(target)}`;
};

// Circuit breaker for the auth-expired → login redirect. `handleAuthExpired`
// does a hard navigation, which wipes all in-memory state, so the counter must
// live in sessionStorage to survive the reload and actually bound a ping-pong.
// Each full page load counts as at most one cycle (see the in-memory guard in
// handleAuthExpired), so this counts redirect *cycles*, not individual 401s.
const AUTH_REDIRECT_KEY = 'bkfc.authExpiredRedirects';
// 30s window (not 10s): a real loop is full-page reload cycles that each take a
// couple of seconds, so a short window could reset before the count trips.
const AUTH_REDIRECT_WINDOW_MS = 30_000;
const AUTH_REDIRECT_MAX = 3;

// Returns true if it is still safe to redirect; false once too many redirects
// have happened inside the window (break the loop instead of reloading forever).
const registerAuthExpiredRedirect = () => {
  let prev = null;
  try {
    prev = JSON.parse(sessionStorage.getItem(AUTH_REDIRECT_KEY) || 'null');
  } catch (_parseError) {
    // Corrupt/tampered value — drop it and start a fresh window rather than
    // failing open (leaving bad JSON in place) for the rest of the session.
    try {
      sessionStorage.removeItem(AUTH_REDIRECT_KEY);
    } catch (_removeError) {
      /* ignore */
    }
    prev = null;
  }
  try {
    const now = Date.now();
    const withinWindow =
      prev && typeof prev.firstAt === 'number' && now - prev.firstAt < AUTH_REDIRECT_WINDOW_MS;
    const firstAt = withinWindow ? prev.firstAt : now;
    const count = (withinWindow ? prev.count : 0) + 1;
    sessionStorage.setItem(AUTH_REDIRECT_KEY, JSON.stringify({ firstAt, count }));
    return count <= AUTH_REDIRECT_MAX;
  } catch (_storageError) {
    // sessionStorage unavailable (private mode, etc.) — don't block the
    // redirect; logout-before-redirect is the primary defense anyway.
    return true;
  }
};

const clearAuthExpiredRedirects = () => {
  try {
    sessionStorage.removeItem(AUTH_REDIRECT_KEY);
  } catch (_storageError) {
    /* ignore */
  }
};

// One page load = at most one auth-expiry handling. A hard nav resets this to
// false, so it does not suppress the *next* cycle — it only collapses the burst
// of concurrent dspAuthExpired events a single page can emit (e.g. several
// authFetch calls 401ing at once) into one logout + one redirect + one count.
let authExpiredInFlight = false;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const session = await dspAuthService.getSession();
      const authenticated = !!session?.isAuthenticated;
      const sessionUser = authenticated ? session?.user || null : null;
      // Reflect auth state in analytics context (no login event — this is a
      // silent session restore, not an explicit login).
      analytics.setUserContext({ logged_in: authenticated });

      if (!authenticated) {
        clientTokenCache.invalidate();
        // Cold-start with a dead/expired session: also drop the PERSISTED client
        // token so initializeService()/getClientToken() can't reuse a stale one
        // on this boot and recreate the 403 auth/refresh loop.
        spotlightService.clearClientToken();
        setIsAuthenticated(false);
        setUser(null);
        setError(null);
        return session;
      }

      if (sessionUser) {
        try {
          const clientToken = await clientTokenCache.getClientToken();
          if (clientToken) {
            // clientTokenCache.getClientToken already stored the token; just wire
            // the user context so the service can resolve it for requests.
            spotlightService.setCurrentUser(sessionUser);
          }
        } catch (_tokenError) {
          // Non-fatal — token will be fetched lazily later
        }
      }

      setIsAuthenticated(true);
      setUser(sessionUser);
      setError(null);
      return session;
    } catch (sessionError) {
      console.error('AuthContext: Error restoring session:', sessionError);
      setError(new Error("We couldn't verify your session. Please refresh and try again."));
      setIsAuthenticated(false);
      setUser(null);
      clientTokenCache.invalidate();
      spotlightService.clearClientToken();
      throw sessionError;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh().catch(refreshError => {
      console.warn('Failed to rehydrate auth session', refreshError);
    });
  }, [refresh]);

  useEffect(() => {
    const handleAuthExpired = async () => {
      // Collapse the burst of concurrent dspAuthExpired events a single page can
      // emit into one handling; a hard nav resets this, so the next cycle still
      // runs (and is bounded by the sessionStorage breaker below).
      if (authExpiredInFlight) {
        return;
      }
      authExpiredInFlight = true;

      // Decide + count the redirect SYNCHRONOUSLY, before any await or React
      // state change, so nothing (a preempting redirect, an aborted logout) can
      // stop the breaker from advancing.
      const mayRedirect = registerAuthExpiredRedirect();

      clientTokenCache.invalidate();
      // Also drop the PERSISTED client token (localStorage), not just the
      // in-memory cache — otherwise the stale token survives the redirect and
      // the legacy direct-DSP path resurrects the auth/refresh failure loop.
      spotlightService.clearClientToken();

      // Terminate the SERVER session and let it COMPLETE before touching React
      // auth state. Clearing only the client token left the bkfc_session cookie
      // valid, so /auth/session still answered "authenticated" and LoginPage
      // bounced back — a redirect ping-pong / unbounded reload loop. Crucially,
      // we must NOT set isAuthenticated=false yet: <ProtectedRoute> hard-redirects
      // to /login on !isAuthenticated *during render*, so flipping it while the
      // logout is in flight would navigate away first — aborting the logout
      // request and leaving the cookie intact, i.e. the exact bug. /auth/logout
      // clears the iron-session cookie server-side and does NOT depend on the
      // DSP, so it succeeds even during the token/DSP outage that triggers this.
      try {
        await dspAuthService.logout();
      } catch (_logoutError) {
        // Non-fatal: proceed regardless; the breaker bounds any residual loop
        // if the cookie somehow can't be cleared.
      }

      if (!mayRedirect) {
        // Loop breaker tripped: stop the reload storm. Deliberately do NOT flip
        // auth state — that would let ProtectedRoute resume redirecting. Release
        // the guard so a later, post-window expiry is still handled.
        authExpiredInFlight = false;
        return;
      }

      // Session is now cleared. Flip state and navigate with NO await in
      // between, so the synchronous assign wins before React can re-render
      // ProtectedRoute off the state change.
      setIsAuthenticated(false);
      setUser(null);
      window.location.assign(toLoginPath(getCurrentPath()));
    };

    window.addEventListener('dspAuthExpired', handleAuthExpired);
    return () => {
      window.removeEventListener('dspAuthExpired', handleAuthExpired);
    };
  }, []);

  const primeClientToken = (payload, fallbackUser) => {
    const clientToken = payload?.clientToken;
    if (!clientToken) {
      return;
    }
    // Seed the in-memory token (with its real expiry) directly into the cache;
    // the service reads from there.
    clientTokenCache.seed(clientToken, payload?.expiresAt);
    if (fallbackUser) {
      spotlightService.setCurrentUser(fallbackUser);
    }
  };

  const login = useCallback(async (email, password) => {
    const payload = await dspAuthService.login(email, password);
    const loggedInUser = payload?.user || { email };
    primeClientToken(payload, loggedInUser);
    // Anything cached before this point was answered for a different (usually
    // anonymous) viewer, and catalogue bodies carry that viewer's entitlement —
    // `APP_LOAD_DATA` lives in localStorage, so it outlives the login screen
    // that fetched it. Drop them, or the subscriber who just signed in reads
    // BUY NOW off the anonymous copy until its TTL lapses.
    spotlightService.clearEntitlementScopedCache();
    // A fresh, successful login clears the auth-expiry redirect breaker so a
    // later, unrelated expiry gets its normal redirect budget again.
    clearAuthExpiredRedirects();
    setIsAuthenticated(true);
    setUser(loggedInUser);
    setError(null);
    analytics.setUserContext({ logged_in: true });
    analytics.trackLogin('password');
    return payload;
  }, []);

  const register = useCallback(async profile => {
    const payload = await dspAuthService.register(profile);
    const registeredUser = payload?.user || { email: profile?.email };
    primeClientToken(payload, registeredUser);
    spotlightService.clearEntitlementScopedCache();
    setIsAuthenticated(true);
    setUser(registeredUser);
    setError(null);
    analytics.setUserContext({ logged_in: true });
    analytics.trackSignUp('password');
    return payload;
  }, []);

  const logout = useCallback(async () => {
    try {
      await dspAuthService.logout();
    } finally {
      clientTokenCache.invalidate();
      // Clear SpotlightService's in-memory client token + cached identity too.
      // The token now lives only in memory (no localStorage), so clearing the
      // session cache alone would leave `spotlightService.clientToken` holding
      // the previous account's credential — which would keep authenticating
      // direct DSP calls (cookie-independent) after logout / when switching
      // accounts. Mirrors the refresh() and dspAuthExpired reset paths.
      spotlightService.clearClientToken();
      spotlightService.clearCurrentUser();
      // The catalogue this account saw was unlocked for this account; the next
      // viewer of this browser must not inherit it.
      spotlightService.clearEntitlementScopedCache();
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      analytics.trackLogout();
      analytics.setUserContext({ logged_in: false });
    }
  }, []);

  const requestPasswordReset = useCallback(async email => {
    await dspAuthService.requestPasswordReset(email);
  }, []);

  const updatePassword = useCallback(async payload => {
    await dspAuthService.updatePassword(payload);
  }, []);

  const getAccessTokenSilently = useCallback(async () => {
    return clientTokenCache.getClientToken();
  }, []);

  const loginWithRedirect = useCallback(options => {
    const returnTo = options?.appState?.returnTo || getCurrentPath();
    window.location.assign(toLoginPath(returnTo));
  }, []);

  const value = useMemo(() => {
    return {
      user,
      isAuthenticated,
      isLoading,
      error,
      login,
      register,
      logout,
      refresh,
      requestPasswordReset,
      updatePassword,
      getAccessTokenSilently,
      loginWithRedirect,
      clearError: () => setError(null),
    };
  }, [
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    refresh,
    requestPasswordReset,
    updatePassword,
    getAccessTokenSilently,
    loginWithRedirect,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used inside AuthProvider');
  }
  return context;
};

export const useAuth0Compat = () => useAuthContext();
